import EventManager from '../eventManager/EventManager'
import type WebModel from '../models/WebModel'
import { IBidResponse, INativeResponse } from '../../typings/IPrebid'
import SlotRepository from '../slots/SlotRepository'
import type { ISlotTargeting } from '../slots/Slot'
import { AdType, IAdType } from '../enums/AdType'
import { isInBannerNativeAd, isNative } from '../prebidFacade/BidderSettings'
import { renderAdSafeframe, renderAdNonSafeframe } from './renderAd'
import { NativeRenderer } from '../native/NativeRenderer'
import { getOutstreamPlayerSize } from '../outstream/hasOutstream'

enum MessageType {
  creativeDelivery = 'creativeDelivery'
}

export default class InjectableCreative {
  private bidsByAdId: { [adId: string]: IBidResponse } = {}

  constructor(readonly model: WebModel, pbjs = window.pbjs) {
    addEventListener(
      'message',
      (event) => this.listenAdRequestFromCreative(event),
      false
    )
    pbjs.onEvent(EventManager.events.bidResponse, (bid: IBidResponse) => {
      this.bidsByAdId[bid.adId] = bid
    })
  }

  /** This event occurs after the slot render ended event */
  private listenAdRequestFromCreative(event): void {
    // Get data from message
    let data: { message: string; targeting: string }
    let targeting: { [key in keyof ISlotTargeting]: Array<string> }
    try {
      data = JSON.parse(event.data)
      // Check if from our code
      if (data.message !== 'requestFunction') {
        return
      }

      targeting = JSON.parse(data.targeting)
    } catch {
      return
    }

    const adId = (targeting.hb_adid && targeting.hb_adid[0]) || ''
    const slotId = (targeting.slot_id && targeting.slot_id[0]) || ''

    // Get requested bidResponse
    const bidResponse = this.bidsByAdId[adId]
    const slot = SlotRepository.getSlotById(slotId)

    if (!bidResponse || !slot) {
      return
    }

    slot.adType = getPrebidAdType(bidResponse)
    if (bidResponse.isOutstream) {
      // We don't really care what the bidResponse says the dimensions are, so use our own.
      // Some bids come back as 1x1 or undefined-x-undefined, so we need to do this anyway.
      const [width, height] = getOutstreamPlayerSize(
        slot,
        slot.target?.parentElement
      )
      slot.height = height
      slot.width = width
    } else {
      slot.height = bidResponse.height
      slot.width = bidResponse.width
    }
    slot.sizeContainers()

    // We're skipping Prebid's renderAd function which emits the `bidWon` event
    // so let's emit 'bidWon' event here
    if (isSafeframe(targeting) || isNative(bidResponse)) {
      EventManager.trigger(EventManager.events.bidWon, bidResponse)
    }

    /* 
        Create our Function Message.
        The functionStr is our encoded, stringified function to execute.
        Arguments needed to execute are in the args array.
        Our typing enforces the correct arguments are given to execute the 
        specified function.
        */
    let renderingFunction: Function =
      targeting.hb_safeframe && targeting.hb_safeframe[0] === '0'
        ? renderAdNonSafeframe
        : renderAdSafeframe

    if (isNative(bidResponse)) {
      const native = bidResponse.native as INativeResponse
      const payload = { slot, native }
      renderingFunction = () => ({
        /* no-op */
      })

      const nativeRenderer = new NativeRenderer({ slot, bidResponse })
      nativeRenderer.render()
    }

    const message: IFunctionMessage<
      typeof renderAdSafeframe | typeof renderAdNonSafeframe
    > = {
      args: [bidResponse],
      functionStr: encodeURI(renderingFunction.toString()),
      type: MessageType.creativeDelivery
    }

    /* Send it to the iframe to be executed */
    sendFunctionMessageToSource(event, message)
  }
}

function isSafeframe(targeting: {
  amznbid?: string[] | undefined
  amzniid?: string[] | undefined
  amznsize?: string[] | undefined
  bidFloor?: string[] | undefined
  dynFloor?: string[] | undefined
  hb_bid?: string[] | undefined
  hb_bidder?: string[] | undefined
  hb_count?: string[] | undefined
  hb_pmp?: string[] | undefined
  hb_pool?: string[] | undefined
  ad_size?: string[] | undefined
  lazy?: string[] | undefined
  maxVelocity?: string[] | undefined
  OE?: string[] | undefined
  offsetMultiplier?: string[] | undefined
  pid?: string[] | undefined
  player_group?: string[] | undefined
  psa?: string[] | undefined
  pp?: string[] | undefined
  refresh_count?: string[] | undefined
  refresh?: string[] | undefined
  slot_number?: string[] | undefined
  slot?: string[] | undefined
  slot_id?: string[] | undefined
  timeout?: string[] | undefined
  UR?: string[] | undefined
  URP?: string[] | undefined
  velocity?: string[] | undefined
  sizeLift?: string[] | undefined
  hb_pb_amazon?: string[] | undefined
  hb_pb_appnexus?: string[] | undefined
  hb_pb_appnexusAst?: string[] | undefined
  hb_pb_indexExchange?: string[] | undefined
  hb_pb_rubicon?: string[] | undefined
  hb_pb_triplelift?: string[] | undefined
  hb_liv?: string[] | undefined
  hb_safeframe?: string[] | undefined
  ccpa?: string[] | undefined
  partnerLift?: string[] | undefined
  gid?: string[] | undefined
  hb_advertiser?: string[] | undefined
  hb_adid?: string[] | undefined
  hb_pb?: string[] | undefined
  hb_s2sBidder?: string[] | undefined
  hb_ub?: string[] | undefined
}) {
  return targeting.hb_safeframe && targeting.hb_safeframe[0] === '1'
}

/* 
  T is the function being stringified.
*/
function sendFunctionMessageToSource<T extends (...args) => any>(
  { source }: { source: Window | ServiceWorker | MessagePort | null },
  message: IFunctionMessage<T>
): void {
  if (!source) {
    return
  }
  // @ts-ignore
  source.postMessage(JSON.stringify(message), '*')
}

interface IMessage {
  type: MessageType
}

/* Where T is the function being stringified */
interface IFunctionMessage<T extends (...args) => any> extends IMessage {
  functionStr: string
  type: MessageType.creativeDelivery
  args: Parameters<T>
}

function getPrebidAdType(bidResponse: IBidResponse): IAdType {
  if (bidResponse.native) {
    return AdType.prebidNative as IAdType
  }

  if (isInBannerNativeAd(bidResponse)) {
    return AdType.inBannerNative as IAdType
  }

  return AdType.prebidStandard as IAdType
}
